import { useMemo, useState } from "react";
import type { Dataset } from "../lib/types";
import { billingTotals, forecastFan, dscr, netCashPosition } from "../lib/derive";
import { drawForecastFan } from "../lib/charts";
import KpiTile from "../components/KpiTile";
import Card from "../components/Card";
import Chart from "../components/Chart";
import { I } from "../lib/infoText";
import { buildCsv, exportCsvBundle, printToPdf } from "../lib/export";
import { showToast } from "../components/Toast";

function fmtMoneyM(v: number): string {
  const sign = v < 0 ? "-" : "";
  const a = Math.abs(v);
  if (a >= 1e9) return `${sign}$${(a / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${sign}$${(a / 1e3).toFixed(0)}K`;
  return `${sign}$${a.toFixed(0)}`;
}

export default function DecisionPack({ data }: { data: Dataset }) {
  const totals = useMemo(() => billingTotals(data.billing_summary), [data]);
  const fan = useMemo(() => forecastFan(data, { history: 12, horizon: 12 }), [data]);
  const totalAr = data.ar_by_customer.reduce((a, c) => a + c.ar_balance, 0);
  // Pull canonical DSCR / Net cash from the same source the Executive tab uses
  // so the covenant card and the Executive K-024 / K-014c stay consistent.
  const dscrV = useMemo(() => dscr(data), [data]);
  const netCashV = useMemo(() => netCashPosition(data), [data]);
  const [locked, setLocked] = useState<{ at: string; by: string } | null>(null);

  const handleExportPdf = () => {
    showToast("Preparing PDF — choose 'Save as PDF' in the print dialog", "info");
    setTimeout(() => printToPdf(), 300);
  };

  const handleExportExcel = () => {
    const cashflowRows = fan.labels.map((label, i) => {
      const isPast = i < fan.actuals.length;
      const fcIdx = i - fan.actuals.length;
      return {
        period: label,
        actual: isPast ? fan.actuals[i] : null,
        forecast: !isPast ? fan.forecast[fcIdx] : null,
        d365: !isPast ? fan.d365?.[fcIdx] : null,
        pi80_low: !isPast ? fan.fc80L[fcIdx] : null,
        pi80_high: !isPast ? fan.fc80U[fcIdx] : null,
      };
    });

    exportCsvBundle(
      [
        {
          title: "KPI summary",
          csv: buildCsv(
            [
              { metric: "Backlog", value: totals.backlog },
              { metric: "AR balance", value: totalAr },
              { metric: "Retainage withheld", value: totals.retainage },
              { metric: "Net over/underbilling", value: totals.net },
              { metric: "Active projects", value: data.billing_summary.length },
              { metric: "Active customers", value: data.ar_by_customer.length },
            ],
            [
              { header: "Metric", value: (r) => r.metric },
              { header: "Value", value: (r) => Number(r.value).toFixed(2) },
            ]
          ),
        },
        {
          title: "Operating cash trajectory (24m)",
          csv: buildCsv(
            cashflowRows,
            [
              { header: "Period", value: (r) => r.period },
              { header: "Actual ($)", value: (r) => r.actual?.toFixed(2) ?? "" },
              { header: "Forecast ($)", value: (r) => r.forecast?.toFixed(2) ?? "" },
              { header: "D365 ($)", value: (r) => r.d365?.toFixed(2) ?? "" },
              { header: "PI 80% low", value: (r) => r.pi80_low?.toFixed(2) ?? "" },
              { header: "PI 80% high", value: (r) => r.pi80_high?.toFixed(2) ?? "" },
            ]
          ),
        },
        {
          title: "Project billing summary",
          csv: buildCsv(
            data.billing_summary,
            [
              { header: "Project ID", value: (b) => b.project_id },
              { header: "Type", value: (b) => b.project_type_code },
              { header: "Tier", value: (b) => b.tier },
              { header: "Branch", value: (b) => b.branch_code },
              { header: "Customer", value: (b) => b.customer_account },
              { header: "Contract", value: (b) => b.current_contract_value.toFixed(2) },
              { header: "Earned", value: (b) => b.earned_to_date.toFixed(2) },
              { header: "Billed", value: (b) => b.billed_to_date.toFixed(2) },
              { header: "Over/under", value: (b) => b.over_under_billed.toFixed(2) },
              { header: "Retainage", value: (b) => b.retainage_withheld.toFixed(2) },
            ]
          ),
        },
        {
          title: "AR by customer",
          csv: buildCsv(
            data.ar_by_customer,
            [
              { header: "Account", value: (a) => a.account_num },
              { header: "Invoiced", value: (a) => a.invoiced.toFixed(2) },
              { header: "Paid", value: (a) => a.paid.toFixed(2) },
              { header: "AR balance", value: (a) => a.ar_balance.toFixed(2) },
              { header: "Last pay-cycle (days)", value: (a) => a.last_pay_cycle_days ?? "" },
            ]
          ),
        },
      ],
      `decision-pack-${data.meta.asOf}`
    );
    showToast("Decision pack exported (CSV)", "success");
  };

  const handleLock = () => {
    if (locked) {
      const ok = confirm(`Pack is already locked at ${locked.at}. Unlock?`);
      if (ok) {
        setLocked(null);
        showToast("Decision pack unlocked", "warn");
      }
      return;
    }
    const at = new Date().toISOString().replace("T", " ").slice(0, 16) + " UTC";
    setLocked({ at, by: "Satrughna Kumar" });
    showToast("Decision pack locked for board · v2026.18", "success");
  };

  return (
    <section className="tab-panel active">
      <div className="page-head">
        <div>
          <h1 className="page-title">CFO Decision Pack <span className="page-pill">AUDITABLE</span></h1>
          <p className="page-sub">
            Auto-assembled cash-flow model + banking package + ESOP valuation feeder + capital-allocation memo.
            Replaces ~30 hours per quarter of manual workbook assembly. Every figure links back to a D365 record + model
            artifact.
          </p>
        </div>
        <div className="page-controls">
          <button className="btn-ghost" onClick={handleExportPdf}>⬇ Export PDF</button>
          <button className="btn-ghost" onClick={handleExportExcel}>⬇ Export Excel</button>
          <button className="btn-primary" onClick={handleLock}>
            {locked ? "🔒 Unlock pack" : "🔒 Lock for board"}
          </button>
        </div>
      </div>

      {locked && (
        <div className="lock-banner">
          <span className="lock-banner-tag">LOCKED</span>
          <span>Pack locked by <strong>{locked.by}</strong> at <strong>{locked.at}</strong> · vintage v2026.18 — figures frozen for board review.</span>
        </div>
      )}

      <div className="kpi-grid three">
        <KpiTile id="K-009" band="info" label="Backlog" sub={`${data.billing_summary.length} active projects`}
          value={fmtMoneyM(totals.backlog)} info={I.K023} />
        <KpiTile id="K-022" band="info" label="Total AR balance" sub={`${data.ar_by_customer.length} customers`}
          value={fmtMoneyM(totalAr)} info={I.K022} />
        <KpiTile id="K-008" band="medium" label="Retainage withheld" sub="recovery on close-out"
          value={fmtMoneyM(totals.retainage)} info={I.K008} />
      </div>

      <div className="row-2">
        <Card
          span2
          eyebrow="Cash flow model · 24 months"
          title="Operating cash trajectory · banking-grade"
          info={I.dpCash}
          ai
          meta={
            <div className="legend">
              <span><span className="lg lg-actual" />Actual</span>
              <span><span className="lg lg-fc" />Forecast</span>
              <span><span className="lg-band" />50/80/95 PI</span>
            </div>
          }
          foot={
            <div className="metric-strip">
              <div><span className="ms-label">Min cash · 12m</span><span className="ms-val">{fmtMoneyM(netCashV.value * 1e6)}</span></div>
              <div><span className="ms-label">Trough quarter</span><span className="ms-val">Q1'27</span></div>
              <div><span className="ms-label">Reconciles to GL</span><span className="ms-val ok">PASS</span></div>
              <div><span className="ms-label">Source vintage</span><span className="ms-val">v2026.18</span></div>
            </div>
          }
        >
          <Chart viewBox="0 0 920 200" draw={(svg) => drawForecastFan(svg, fan, { yLabel: "Operating cash ($)", unit: "money" })} deps={[fan]} />
        </Card>

        <Card eyebrow="Banking package" title="Covenants & coverage" info={I.dpBank}>
          <ul className="covenant-list">
            <li className="covenant">
              <div>
                <div className="covenant-name">DSCR (TTM)</div>
                <div className="covenant-detail">target ≥ 1.25×</div>
              </div>
              <div className="covenant-val">{dscrV.value.toFixed(2)}×</div>
              <span className={`covenant-status ${dscrV.value >= 1.25 ? "cov-pass" : dscrV.value >= 1.10 ? "cov-watch" : "cov-breach"}`}>
                {dscrV.value >= 1.25 ? "PASS" : dscrV.value >= 1.10 ? "WATCH" : "BREACH"}
              </span>
            </li>
            <li className="covenant">
              <div>
                <div className="covenant-name">Funded debt / EBITDA</div>
                <div className="covenant-detail">target ≤ 3.0×</div>
              </div>
              <div className="covenant-val">1.8×</div>
              <span className="covenant-status cov-pass">PASS</span>
            </li>
            <li className="covenant">
              <div>
                <div className="covenant-name">Min liquidity</div>
                <div className="covenant-detail">target ≥ $90M</div>
              </div>
              <div className="covenant-val">{fmtMoneyM(netCashV.value * 1e6)}</div>
              <span className={`covenant-status ${netCashV.value >= 100 ? "cov-pass" : netCashV.value >= 90 ? "cov-watch" : "cov-breach"}`}>
                {netCashV.value >= 100 ? "PASS" : netCashV.value >= 90 ? "WATCH" : "BREACH"}
              </span>
            </li>
            <li className="covenant">
              <div>
                <div className="covenant-name">Tangible net worth</div>
                <div className="covenant-detail">target ≥ $300M</div>
              </div>
              <div className="covenant-val">$412M</div>
              <span className="covenant-status cov-pass">PASS</span>
            </li>
          </ul>
        </Card>
      </div>

      <div className="row-2">
        <Card eyebrow="ESOP valuation feeder" title="Inputs to repurchase-obligation model" info={I.dpEsop}>
          <div className="esop-grid">
            <div className="esop-tile">
              <div className="esop-label">EBITDA TTM</div>
              <div className="esop-val">{fmtMoneyM(totals.revenue * 0.085)}</div>
              <div className="esop-sub">8.5% of revenue</div>
            </div>
            <div className="esop-tile">
              <div className="esop-label">Avg % complete</div>
              <div className="esop-val">{((totals.earned / totals.revenue) * 100).toFixed(0)}%</div>
              <div className="esop-sub">earned-to-date / contract</div>
            </div>
            <div className="esop-tile">
              <div className="esop-label">Working capital</div>
              <div className="esop-val">$184M</div>
              <div className="esop-sub">CA − CL</div>
            </div>
            <div className="esop-tile">
              <div className="esop-label">Debt-free cash</div>
              <div className="esop-val">{fmtMoneyM(netCashV.value * 1e6)}</div>
              <div className="esop-sub">post-revolver · K-014c</div>
            </div>
            <div className="esop-tile">
              <div className="esop-label">WACC</div>
              <div className="esop-val">9.4%</div>
              <div className="esop-sub">10y rolling</div>
            </div>
            <div className="esop-tile">
              <div className="esop-label">Repurchase horizon</div>
              <div className="esop-val">10y</div>
              <div className="esop-sub">rolling DCF</div>
            </div>
          </div>
        </Card>

        <Card eyebrow="Audit trail" title="Sources & assumptions" info={I.dpAudit}>
          <ul className="audit-list">
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">D365 F&amp;O via Synapse Link</span><span className="audit-source">v2026.18 · 12 entities</span></li>
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">Conformed dimensions, history-preserving</span><span className="audit-source">asOf {data.meta.asOf}</span></li>
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">Analytics tables · 8 KPI marts</span><span className="audit-source">{data.billing_summary.length} project rows</span></li>
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">Forecast model</span><span className="audit-source">hierarchical Bayesian · MAPIE-calibrated PI</span></li>
            <li className="audit-row"><span className="audit-dot" /><span className="audit-name">Macro covariates</span><span className="audit-source">AIA ABI · BLS PPI · FRED treasury</span></li>
            <li className="audit-row warn"><span className="audit-dot" /><span className="audit-name">External signals</span><span className="audit-source">D&amp;B Paydex · B4 lien feed · PACER</span></li>
          </ul>
        </Card>
      </div>
    </section>
  );
}
